import React, { useCallback } from 'react';
import { getFullscreenElement, useIsFullscreen } from './useIsFullscreen';

export type FullscreenButtonProps = {
  targetRef: React.RefObject<HTMLElement>;
};

/** Cross-browser fullscreen API wrapper. */
type FsDocument = Document & {
  webkitExitFullscreen?: () => Promise<void>;
};
type FsElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void>;
};

/**
 * Small icon button that enters / exits fullscreen on the referenced element.
 * Uses the browser Fullscreen API with WebKit fallback.
 */
export const FullscreenButton: React.FC<FullscreenButtonProps> = ({ targetRef }) => {
  const isFullscreen = useIsFullscreen(targetRef);

  const toggle = useCallback(() => {
    const el = targetRef.current as FsElement | null;
    if (!el) return;
    const fs = getFullscreenElement();
    if (fs === null) {
      if (el.requestFullscreen) {
        el.requestFullscreen().catch(() => undefined);
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen().catch(() => undefined);
      }
    } else {
      const d = document as FsDocument;
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => undefined);
      } else if (d.webkitExitFullscreen) {
        d.webkitExitFullscreen().catch(() => undefined);
      }
    }
  }, [targetRef]);

  return (
    <button
      type="button"
      className="as-fs-btn"
      onClick={toggle}
      aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
      title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
    >
      {isFullscreen ? (
        // Collapse icon
        <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M6 6H2V4h2V2h2zm4 0V2h2v2h2v2zM6 10v4H4v-2H2v-2zm4 0h4v2h-2v2h-2z" />
        </svg>
      ) : (
        // Expand icon
        <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M2 2h5v2H4v3H2zm12 0v5h-2V4H9V2zM2 9h2v3h3v2H2zm12 0v5H9v-2h3V9z" />
        </svg>
      )}
    </button>
  );
};
